/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {InfoField, InfoTwoColumnGrid} from './InfoTwoColumnGrid';

export interface InfoSectionProps {
  fields: InfoField[];
  labelColor?: string;
}

const useStyles = makeStyles(() => ({
  section : {
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'flex-start',
    width           : '100%',
    padding         : '8px',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey7')}`,
    borderRadius    : '8px'
  }
}));

/**
 * InfoSection renders one bordered Additional Info group (Asset Profile,
 * Vehicle Details, or Device Details per ION-7466) - its fields laid out in
 * `InfoTwoColumnGrid`'s two-column grid
 * (`@ion/dls/src/components/assetdetails/current/InfoTwoColumnGrid`). Per
 * Figma, the group has no title of its own - the three groups are
 * distinguished only by their bordered cards and, where applicable, their
 * `labelColor`. `labelColor` is forwarded to the grid unchanged since the
 * Figma export shows Asset Profile's field labels in `grey3` (`#767676`)
 * while Vehicle Details and Device Details use a slightly darker `#6E6E6E`
 * (`dlsTextColor('grey')`) - it is not one color across all three sections.
 *
 * ```typescript
 *
 *   import {InfoSection} from
 *     '@ion/dls/src/components/assetdetails/current/InfoSection';
 *   ...
 *
 *   <InfoSection fields={[
 *       {label: 'Name', value: 'Vehicle 13 Vision'},
 *       {label: 'Install Date', value: 'Date format'}
 *     ]} />
 *
 * ```
 *
 */
export const InfoSection = ({
  fields,
  labelColor
}: InfoSectionProps) => {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <InfoTwoColumnGrid fields={fields}
        labelColor={labelColor} />
    </div>
  );
};

export default React.memo(InfoSection);
